import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { FlowActionId, FlowStepId } from '../woz-flow.types';
import type { FlowAsyncHandler, FlowContext, FlowStepDefinition, FlowStepRef } from '../woz-flow.types';

/** Main step — triggers classification of user complaints. */
export const classifyComplaintsStep: FlowStepDefinition = {
  id: FlowStepId.ClassifyComplaints,
  type: 'async',
  skipIf: (ctx: FlowContext) => !!ctx.classification,
  thinkingMessages: [
    'Ok, laten we je klacht analyseren...',
    'Ik ga kijken of er aanvullende informatie is die ik graag van je wil weten...',
    'Even geduld...',
    'Bijna klaar met analyseren...',
  ],
};

/** Shown when the classification API call fails. */
export const classificationErrorStep: FlowStepDefinition = {
  id: FlowStepId.ClassificationError,
  type: 'message',
  text: 'Classificatie mislukt. Probeer het opnieuw.',
};

/** Shown when classification returns no complaints. */
export const classificationFailedStep: FlowStepDefinition = {
  id: FlowStepId.ClassificationFailed,
  type: 'message',
  text: 'Ik kon je bezwaar helaas niet classificeren. Probeer het opnieuw met een gedetailleerdere beschrijving.',
};

/** Shown when classification returns complaints but none are relevant to WOZ. */
export const classificationNoRelevantStep: FlowStepDefinition = {
  id: FlowStepId.ClassificationNoRelevant,
  type: 'message',
  text: 'Ik kon helaas geen relevante WOZ-bezwaren vinden in je beschrijving.',
};

/** Action step letting the user retry with a new description or finish the flow. */
export const noRelevantRetryOrFinishStep: FlowStepDefinition = {
  id: FlowStepId.NoRelevantRetryOrFinish,
  type: 'action',
  text: 'Wil je het opnieuw proberen met een andere beschrijving?',
  actions: [
    {
      id: FlowActionId.NoRelevantRetry,
      label: 'Ja, opnieuw proberen',
      event: 'trigger',
      payload: FlowStepId.NoRelevantRetryOrFinish,
    },
    {
      id: FlowActionId.NoRelevantFinish,
      label: 'Nee, dit was alles',
      event: 'trigger',
      payload: FlowStepId.NoRelevantRetryOrFinish,
    },
  ],
  disableInput: true,
  waitForTrigger: FlowStepId.NoRelevantRetryOrFinish,
};

/** Retry input — asks the user to re-describe their complaint. */
export const describeComplaintsRetryStep: FlowStepDefinition = {
  id: FlowStepId.DescribeComplaintsRetry,
  type: 'input',
  text: 'Beschrijf je bezwaar opnieuw:',
  contextKey: 'complaints',
};

/** Confirmation step for a single (or no relevant) complaint. */
export const confirmSingleComplaintStep = (complaintText: string): FlowStepDefinition => ({
  id: FlowStepId.ConfirmSingleComplaint,
  type: 'message',
  text: `Ik heb je bezwaar begrepen:\n\n> ${complaintText}\n\nWe gaan hiermee verder.`,
});

/** Choice step for multiple complaints — user picks treatment mode. */
export const chooseComplaintModeStep = (complaintList: string): FlowStepDefinition => ({
  id: FlowStepId.ChooseComplaintMode,
  type: 'action',
  text: `Ik zie dat je meerdere punten noemt. Wil je deze opsplitsen zodat we elk punt apart kunnen behandelen?\n\n${complaintList}`,
  actions: [
    {
      id: FlowActionId.SingleComplaint,
      label: 'Nee, hoort bij elkaar',
      icon: CheckCircleOutlineIcon,
      event: 'trigger',
      payload: FlowStepId.ChooseComplaintMode,
    },
    {
      id: FlowActionId.MultiComplaint,
      label: 'Ja, behandel apart',
      icon: CheckCircleOutlineIcon,
      event: 'trigger',
      payload: FlowStepId.ChooseComplaintMode,
      primaryButton: true,
    },
  ],
  disableInput: true,
  waitForTrigger: FlowStepId.ChooseComplaintMode,
});

/**
 * Create a classify-complaints async step for an additional round.
 * The handler is injected because it lives in WozFlow (to avoid circular imports).
 */
export function createClassifyComplaintsRepeatStep(handler: FlowAsyncHandler): FlowStepDefinition {
  return {
    id: FlowStepId.ClassifyComplaints,
    type: 'async',
    handler,
    thinkingMessages: classifyComplaintsStep.thinkingMessages,
  };
}

/** Rehydrate a classify-complaints step from a persisted FlowStepRef. */
export function rehydrateClassifyComplaintsStep(ref: FlowStepRef, handler: FlowAsyncHandler): FlowStepDefinition {
  return {
    id: FlowStepId.ClassifyComplaints,
    sequence: ref.sequence,
    meta: ref.meta,
    type: 'async',
    handler,
    thinkingMessages: classifyComplaintsStep.thinkingMessages,
  };
}
